import {IItem} from "../../../search/utils/interface";
import React, {useEffect, useState} from "react";
import server from "../../../../../server";
import {
    AssetAllocationSearch,
    AssetAllocationStatus
} from "../../../../../domains/models/asset-management/asset-allocation";
import Grid from "../../../../../common/table";
import {Button, FormInstance, message, Popconfirm, Select, Tag, Tooltip} from "antd";
import {Return_Asset_Status} from "../../../../../constant/constant";
import EmptyData from "../../../../common/empty-data/empty-data";
import {InboxOutlined} from "@ant-design/icons";
import {IterationCw, SearchX} from "lucide-react";
import {removeUnderScore} from "../../../../../utility/utility";

interface IHandleAssetProps {
    selectedRecord: IItem | null;
    onBack: () => void;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    data: IItem[];
    setData: React.Dispatch<React.SetStateAction<IItem[]>>;
    modalForm: FormInstance
}

const HandleAsset = (props: IHandleAssetProps) => {
    const [assets, setAssets] = useState<any[]>([]);
    const [returnStatus, setReturnStatus] = useState("");

    useEffect(() => {
        fetchAllocatedAssets();
    }, [props.selectedRecord?.id])

    async function fetchAllocatedAssets() {
        try {
            const payload: AssetAllocationSearch = {
                employeeId: props.selectedRecord?.id,
                status: [AssetAllocationStatus.ALLOCATED, AssetAllocationStatus.MISSING]
            }
            const response = await server.asset.allocation.search(undefined, undefined, payload);
            setAssets(response);
        } catch (e) {
            console.log("Error while fetching assets", e)
        }
    }

    async function handleAssetReturn(record: any) {
        try {
            await server.asset.return(record.asset.id, returnStatus);
            message.success("Asset returned successfully!")
            await fetchAllocatedAssets();
        } catch (e) {
            console.log("Error while returning asset", e);
        }
    }

    async function handleAssetMissed(record: any) {
        try {
            await server.asset.missing(record.asset.id);
            await fetchAllocatedAssets();
        } catch (e) {
            console.log("Error while submitting missing request", e)
        }
    }

    async function handleDeactivateEmployee() {
        try {
            if (props.selectedRecord) {
                await server.employee.admin.markEmployeeInActive(props.selectedRecord?.id);
                const newData = props.data.filter((item: any) => item.id !== props.selectedRecord?.id);
                message.success("Employee deactivated successfully!")
                props.setData(newData);
                props.setIsModalOpen(false);
                props.modalForm.resetFields();
            }
        } catch (e) {
            console.log("Failed to deactivate employee", e)
        }
    }

    const allocatedAssetsColumns = [
        {
            title: "Category",
            dataIndex: "categoryName",
            key: "categoryName",
            render: (_: any, record: any) => {
                return record?.assetCategory?.name ? record?.assetCategory?.name : "-"
            }
        },
        {
            title: "Asset Code",
            dataIndex: "assetCode",
            key: "assetCode",
            width: 50,
            render: (_: any, record: any) => (
                <Tooltip title={record?.asset?.assetCode}>
            <span className="asset-code-ellipsis">
                {record?.asset?.assetCode ? record.asset.assetCode : "-"}
            </span>
                </Tooltip>
            )
        },
        {
            title: "Returnable",
            dataIndex: "isReturnable",
            key: "isReturnable",
            render: (_: any, record: any) => (
                <p>
                    {record?.asset?.isReturnable ? "Yes" : "No"}
                </p>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: any) => {
                return <Tag className={`asset-allocation-status ${record.status}`}>
                    {record?.status ? removeUnderScore(record.status) : "-"}
                </Tag>

            }
        },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_: any, record: any) => {
                return (
                    <div className={'pn-table-actions'}>
                        <Popconfirm
                            title={"Select status"}
                            onConfirm={() => handleAssetReturn(record)}
                            description={
                                <Select
                                    onChange={(value: any) => setReturnStatus(value)}
                                    placeholder={'Select Asset Status'}>
                                    {(Object.keys(Return_Asset_Status) as Array<keyof typeof Return_Asset_Status>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Return_Asset_Status[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            }
                        >
                            <Tooltip title={"Return Asset"}>
                                <IterationCw className={'employee-deactivate return-button'}/>
                            </Tooltip>

                        </Popconfirm>

                        <Popconfirm
                            okText={"Yes"}
                            title={`Are you sure ${record.asset.assetCode} is missed?`}
                            onConfirm={() => handleAssetMissed(record)}
                            disabled={record?.status === "MISSING"}
                            classNames={{
                                root: "hrms-app asset-confirm"
                            }}
                        >
                            <Tooltip title={"Asset Missing"}>
                                <SearchX
                                    className={`employee-deactivate missing-button ${
                                        record?.status === "MISSING" ? "disabled-icon" : ""
                                    }`}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </div>
                )
            },

        },
    ]

    const handleCancelButton = () => {
        props.setIsModalOpen(false)
    }

    const hasReturnableAssets = assets.some(
        (record: any) => record?.asset?.isReturnable
    );

    return (
        <div>
            {
                assets.length > 0 && <div className="deactivation-note">
                    <p> Please return all assets to inventory.</p>
                </div>
            }
            <div className={'allocated-assets-employee-deactivate-table'}>
                {
                    assets.length > 0 ?
                        <Grid bordered={true}
                              dataSource={assets}
                              columns={allocatedAssetsColumns}
                        />
                        :
                        <EmptyData
                            icon={<InboxOutlined className={'empty-assets-icon'}/>}
                            title={"No Assets Assigned"}
                            subtitle={"This employee can be deactivated."}
                        />
                }
            </div>

            <div className="deactivation-note">
                <p><b>NOTE:</b> If you deactivate the employee, their corresponding user will also be
                    deactivated.</p>
            </div>

            <div className={'deactivate-modal-footer'}>
                <Button
                    className={'cancel-deactivate-button'}
                    onClick={handleCancelButton}
                >
                    Cancel
                </Button>
                <Button
                    className={'back-deactivate-button'}
                    onClick={props.onBack}
                >
                    Back
                </Button>

                <Button
                    className={"ok-deactivate-button"}
                    disabled={hasReturnableAssets}
                    onClick={handleDeactivateEmployee}
                >
                    Yes, Deactivate
                </Button>
            </div>
        </div>
    )
}

export default HandleAsset;